// Utility functions for handling image URLs
// This handles both local images and Cloudinary URLs

/**
 * Get the correct image URL for display
 * @param {string} imageUrl - The image URL from the database
 * @param {string} fallback - Fallback image URL if no image is provided
 * @returns {string} - The complete image URL
 */
export const getImageUrl = (imageUrl, fallback = `${(window.APP_CONFIG?.API_URL || 'https://backendweb.properteck.lk')}/images/no-image.svg`) => {
  // If no image URL provided, return fallback
  if (!imageUrl) return fallback;
  
  // If it's already a complete URL (Cloudinary or other), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If it's a local filename, construct the local URL
//  return `http://localhost:4000/images/${imageUrl}`;
};

/**
 * Get image URL with error handling
 * @param {string} imageUrl - The image URL from the database
 * @param {string} fallback - Fallback image URL
 * @returns {object} - Object with src and onError handler
 */
export const getImageProps = (imageUrl, fallback = `${(window.APP_CONFIG?.API_URL || 'https://backendweb.properteck.lk')}/images/no-image.svg`) => {
  return {
    src: getImageUrl(imageUrl, fallback),
    onError: (e) => {
      e.target.onerror = null;
      e.target.src = fallback;
    }
  };
};

/**
 * Check if an image URL is from Cloudinary
 * @param {string} imageUrl - The image URL to check
 * @returns {boolean} - True if it's a Cloudinary URL
 */
export const isCloudinaryUrl = (imageUrl) => {
  return imageUrl && imageUrl.includes('res.cloudinary.com');
};

/**
 * Get optimized Cloudinary URL with transformations
 * @param {string} imageUrl - The Cloudinary URL
 * @param {object} options - Transformation options
 * @returns {string} - Optimized Cloudinary URL
 */
export const getOptimizedImageUrl = (imageUrl, options = {}) => {
  if (!isCloudinaryUrl(imageUrl)) {
    return getImageUrl(imageUrl);
  }
  
  const {
    width = 'auto',
    height = 'auto',
    quality = 'auto',
    format = 'auto',
    crop = 'limit'
  } = options;
  
  // Insert transformations into the Cloudinary URL
  const parts = imageUrl.split('/upload/');
  if (parts.length === 2) {
    const transformations = `w_${width},h_${height},q_${quality},f_${format},c_${crop}`;
    return `${parts[0]}/upload/${transformations}/${parts[1]}`;
  }
  
  return imageUrl;
};
